<template>
  <GlobalDialog
    :titleCenter="false"
    dialogDefaultSize="70%"
    v-model="showDialog"
    title=""
    :showClose="false"
    :showActionButton="false"
    forceClose
  >
    <template #content>
      <div
        class="relative text-left overflow-hidden transform transition-all w-full"
      >
        <div
          class="flex items-start justify-between gap-4 pb-4 border-b border-untitled-gray-100"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-xl bg-app-primary/10 text-app-primary shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-untitled-gray-900">
                Completed Courses
              </h3>
              <p class="text-sm text-untitled-gray-500 mt-0.5">
                Review the courses you've finished and manage your certificates.
              </p>
            </div>
          </div>
          <button
            type="button"
            @click="showDialog = false"
            class="shrink-0 text-untitled-gray-400 hover:text-untitled-gray-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4 border-b border-untitled-gray-100"
        >
          <p class="text-sm text-untitled-gray-500">
            <span class="font-semibold text-untitled-gray-700">{{
              filteredCourseList.length
            }}</span>
            course<span v-if="filteredCourseList.length !== 1">s</span>
            completed
          </p>
          <div class="flex items-center gap-2 w-full sm:w-auto">
            <label
              class="text-xs font-medium text-untitled-gray-500 whitespace-nowrap"
              >Sort by</label
            >
            <div class="w-full sm:w-[190px]">
              <ECombobox
                v-model="sortBy"
                name="sorts"
                :options="sorts"
                option-name="name"
                value-key="value"
                placeholder="Sort By"
                :multiple="false"
                :filterable="false"
                :clearable="false"
              />
            </div>
          </div>
        </div>
        <LoadingState v-if="loading" class="mx-auto text-center py-12" />
        <div class="" v-else>
          <ul
            v-if="filteredCourseList.length > 0"
            class="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 md:grid-cols-1 grid-cols-1 gap-4 py-4 pr-1"
          >
            <li
              class="flex flex-col rounded-lg border border-untitled-gray-200 bg-white hover:border-untitled-gray-300 hover:shadow-md transition-all"
              v-for="course in filteredCourseList"
              :key="course.id"
            >
              <div class="relative flex-shrink-0" v-if="course">
                <div
                  class="cursor-pointer"
                  @click="openCourse(course.unit?.id)"
                >
                  <img
                    v-if="course.thumbnail"
                    :src="course.thumbnail"
                    class="w-full h-40 object-cover"
                  />
                  <img
                    v-else
                    src="https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg"
                    class="w-full h-40 object-cover"
                  />
                </div>
                <div
                  class="absolute top-2 right-2 inline-flex items-center gap-1 bg-app-primary text-white text-[11px] font-semibold px-2 py-1 rounded-full shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="w-3 h-3"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                  Completed
                </div>
              </div>

              <div class="flex flex-col flex-1 p-4 gap-3">
                <h4
                  class="text-sm font-semibold text-untitled-gray-800 leading-snug"
                >
                  <a
                    href="#"
                    @click.prevent="openCourse(course.unit?.id)"
                    class="hover:text-app-primary transition-colors"
                    >{{ course.name }}</a
                  >
                </h4>

                <div>
                  <div
                    class="flex items-center justify-between text-xs text-untitled-gray-500 mb-1"
                  >
                    <span>Progress</span>
                    <span class="font-medium text-untitled-gray-700"
                      >{{ course.completed_unit_count }}/{{
                        course.unit_count
                      }}
                      Units</span
                    >
                  </div>
                  <div
                    class="w-full h-1.5 bg-untitled-gray-100 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full bg-app-primary rounded-full"
                      :style="{
                        width:
                          (course.unit_count
                            ? (course.completed_unit_count /
                                course.unit_count) *
                              100
                            : 0) + '%',
                      }"
                    ></div>
                  </div>
                </div>

                <div
                  class="mt-auto pt-3 border-t border-untitled-gray-100 flex items-center justify-between gap-2"
                >
                  <template v-if="course.is_certified || course.certificate">
                    <button
                      @click="
                        downloadCertificate(course.certificate.id, 'course')
                      "
                      type="button"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-3 h-3"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                        />
                      </svg>
                      Certificate
                    </button>
                    <p
                      v-if="course.is_certified"
                      class="text-[11px] text-untitled-gray-400 text-right leading-tight"
                    >
                      Certified<br />
                      {{ formatDate(course.certified_at, "MM/DD/YYYY") }}
                    </p>
                  </template>

                  <button
                    v-else
                    @click="openCourse(course.unit?.id)"
                    type="button"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-untitled-gray-700 bg-white border border-untitled-gray-300 hover:bg-untitled-gray-50 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-3 h-3"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                      />
                    </svg>
                    Open Course
                  </button>
                </div>
              </div>
            </li>
          </ul>
          <div
            v-else
            class="flex flex-col items-center justify-center py-12 text-untitled-gray-400"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="w-10 h-10"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
              />
            </svg>
            <p class="mt-3 text-sm font-medium text-untitled-gray-500">
              No completed courses yet.
            </p>
          </div>
        </div>
      </div>
    </template>
  </GlobalDialog>
</template>

<script setup>
import ECombobox from "@/components/element-components/ECombobox";
import {
  ref,
  inject,
  computed,
  defineEmits,
  watch,
  defineProps,
  onMounted,
} from "vue";
import { useRouter } from "vue-router";
import LoadingState from "@/components/partials/LoadingState.vue";
import { useAuthStore } from "@/stores";
import Bugsnag from "@bugsnag/js";
import formatDate from "@/composable/formatDate";
import GlobalDialog from "@/components/layout/GlobalDialog.vue";

const courseList = ref([]);
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
    default: false,
  },
});
const emit = defineEmits(["close", "update:modelValue"]);
const axios = inject("axios");
const router = useRouter();
const auth = useAuthStore();
const loading = ref(false);
const showDialog = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit("update:modelValue", newValue);
  },
});

const sortBy = ref(null);
const sorts = ref([
  {
    name: "A-Z",
    value: "a-z",
  },
  {
    name: "Z-A",
    value: "z-a",
  },
  {
    name: "Highest to Lowest",
    value: "htl",
  },
  {
    name: "Lowest to Highest",
    value: "lth",
  },
]);

watch(sortBy, (val) => {
  changeSort(val);
});

watch(showDialog, (value) => {
  if (value) {
    fetchCompletedCourses();
  }
});

function changeSort(sortBy) {
  courseList.value.sort((a, b) => {
    switch (sortBy) {
      case "a-z":
        return a.name.localeCompare(b.name);
      case "z-a":
        return b.name.localeCompare(a.name);
      case "htl":
        return b.completed_unit_count - a.completed_unit_count;
      case "lth":
        return a.completed_unit_count - b.completed_unit_count;
    }
  });
}

const filteredCourseList = computed(() => {
  return courseList.value;
});

const sendCertificate = async (id, type) => {
  await axios
    .post(`/api/download/certificate`, {
      userId: auth.user?.id,
      id: id,
      type: type,
    })
    .then((response) => {
      window.open(response.data, "_blank");
    })
    .catch((error) => {
      Bugsnag.notify(error);
    });
};

function downloadCertificate(id, type) {
  sendCertificate(id, type);
}

function openCourse(id) {
  router.push(`/units/${id}`);
}

const fetchCompletedCourses = async () => {
  loading.value = true;
  await axios
    .get("/api/v3/user/my-progress/courses-completed")
    .then((response) => {
      courseList.value = response.data;
      loading.value = false;
    })
    .catch((error) => {
      Bugsnag.notify(error);
      loading.value = false;
    });
};

onMounted(() => {
  fetchCompletedCourses();
});
</script>
<style>
iframe {
  width: 100%;
  height: inherit;
}
</style>
